import { useEffect, useRef, useState } from "react";
import { LocaleLink as Link } from "@/components/landing/LocaleLink";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, Check } from "lucide-react";
import { MARKETING_PAGES } from "./flags";

// Banda dedicada a cadenas y franquicias. Replica el patrón visual del hero
// (split copy/visual sobre navy) pero más compacto y con la imagen del kit
// (`/images/chains/imatge_cadenas.webp`) flotando a la derecha con un bob
// suave. La entrada por scroll se activa con IntersectionObserver propio —
// igual que el resto de secciones que cuelgan de .playium-landing.
export default function ForChainsTeaser() {
  const { t } = useTranslation();
  const bullets =
    (t("chains.bullets", { returnObjects: true }) as unknown as string[]) || [];

  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="for-chains"
      ref={ref}
      className={`playium-chains${visible ? " is-visible" : ""}`}
    >
      <div className="playium-chains__card">
       <div className="playium-chains__inner">
        <div className="playium-chains__copy">
          <span className="playium-chains__eyebrow">
            <Building2 aria-hidden="true" />
            {t("chains.eyebrow")}
          </span>

          <h2 className="playium-chains__title playium-heading">
            {t("chains.title")}
          </h2>

          <p className="playium-chains__subtitle">{t("chains.subtitle")}</p>

          <ul className="playium-chains__bullets">
            {bullets.map((b, i) => (
              <li key={i} className="playium-chains__bullet">
                <Check aria-hidden="true" className="playium-chains__check" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {/* CTA a /chain; mientras esa página esté oculta (ver flags.ts) no
              mostramos el botón para no enlazar a una ruta que redirige al hub. */}
          {MARKETING_PAGES.chain && (
            <Link to="/chain" className="playium-chains__cta">
              {t("chains.cta")}
              <ArrowRight aria-hidden="true" />
            </Link>
          )}
        </div>

        <div className="playium-chains__visual">
          {/* Halos decorativos detrás de la imagen (spans vacíos, no se
              anuncian). La imagen sí lleva alt descriptivo. */}
          <span className="playium-chains__glow playium-chains__glow--mint" aria-hidden="true" />
          <span className="playium-chains__glow playium-chains__glow--lavender" aria-hidden="true" />

          {/* LDT-717 · 1200 px para pintarse a 256-855 px (medido de 360 a
              2560 de ancho): en móvil basta la de 640 (40 KB frente a 91). */}
          <img
            src="/images/chains/imatge_cadenas.webp"
            srcSet="/images/chains/imatge_cadenas-640.webp 640w, /images/chains/imatge_cadenas.webp 1200w"
            sizes="(max-width: 767px) 75vw, (max-width: 1023px) 624px, 45vw"
            width={1200}
            height={747}
            alt={t("chains.imageAlt")}
            className="playium-chains__image"
            loading="lazy"
          />
        </div>
       </div>
      </div>
    </section>
  );
}
